import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Modal,
  ModalClose,
  ModalDialog,
  type ModalProps,
} from '@mui/joy';
import classNames from 'classnames';
import { type SxProps } from '@mui/joy/styles/types';
import { useTranslation } from 'react-i18next';

import minimizeTitleContext from '../minimizedWindows/MinimizeTitleContext';
import useMinimizableModal from '../minimizedWindows/useMinimizableModal';
import useToggle, { type UseToggle } from '@/shared/hooks/UseToggle';

import ConfirmationModal from '../../ConfirmationModal';
import MinimizeButton from '../minimizedWindows/MinimizeButton';
import { WarningIcon } from '@/shared/icons/Icons';

type BaseProps = Omit<ModalProps, 'children' | 'onClose' | 'open'>;

const BACKDROP_MODES = {
  CLOSE_ON_CLICK: 'closeOnSingleClick',
  CLOSE_ON_DOUBLE_CLICK: 'closeOnDoubleClick',
  CONFIRM_ON_DOUBLE_CLICK: 'confirmOnDoubleClick',
  NO_CLOSE_ON_CLICK: 'noCloseOnClick',
} as const;

type BackdropBehavior = (typeof BACKDROP_MODES)[keyof typeof BACKDROP_MODES];

type Props<T> = BaseProps &
  React.PropsWithChildren<{
    DialogWrapper?: React.ElementType;
    actions?: React.ReactNode;
    backdropBehavior?: BackdropBehavior;
    confirmCloseOnEscape?: boolean;
    confirmCloseOnClose?: boolean;
    // Merged into the dialog content's sx, on top of the default `overflow: 'auto'`.
    // Needed for content that must overflow the dialog (e.g. a date picker popup),
    // since that default clips anything absolutely positioned past the content box.
    contentSx?: SxProps;
    description?: string;
    disableClose?: boolean;
    hideClose?: boolean;
    icon?: React.ReactNode;
    // When true (and rendered inside a MinimizedWindowsProvider), a minimize
    // button collapses the modal into the bottom dock. The content stays
    // mounted while minimized, so form drafts are preserved. No effect when
    // false or when no provider is present, so other modals are unchanged.
    minimizable?: boolean;
    minimizeIcon?: React.ReactNode;
    minimizeTitle?: string;
    noDivider?: boolean;
    onClose?: () => void;
    scrollGutter?: boolean;
    size?: 'sm' | 'md' | 'lg';
    toggle: UseToggle<T>;
  }>;

const KpcModal = <T,>({
  actions,
  backdropBehavior = 'closeOnDoubleClick',
  children,
  className = 'w-1/2',
  confirmCloseOnEscape,
  confirmCloseOnClose,
  contentSx,
  description,
  disableClose = false,
  DialogWrapper,
  hideClose = false,
  icon,
  minimizable = false,
  minimizeIcon,
  minimizeTitle,
  noDivider = false,
  onClose,
  scrollGutter = false,
  size,
  title,
  toggle,
  ...rest
}: Props<T>) => {
  const { t } = useTranslation();

  const showConfirmationModal = useToggle();

  const handleClose = () => {
    if (disableClose) return;

    onClose?.();
    toggle.hide();
  };

  const { canMinimize, minimize, modalProps, reportTitle } = useMinimizableModal({
    icon,
    minimizable,
    minimizeIcon,
    minimizeTitle,
    onClose: handleClose,
    open: toggle.value,
    title,
  });

  const onClickBackdrop = () => {
    if (disableClose) return;

    if (backdropBehavior === BACKDROP_MODES.CLOSE_ON_CLICK) handleClose();
  };

  const onDoubleClickBackdrop = () => {
    if (disableClose) return;

    if (backdropBehavior === BACKDROP_MODES.CLOSE_ON_DOUBLE_CLICK) handleClose();
    if (backdropBehavior === BACKDROP_MODES.CONFIRM_ON_DOUBLE_CLICK) showConfirmationModal.show();
  };

  const dialog = (
    <ModalDialog className={className} size={size}>
      {!hideClose && <ModalClose className="z-10" disabled={disableClose} />}
      {canMinimize && (
        <MinimizeButton className={hideClose ? 'right-2' : 'right-9'} onClick={minimize} />
      )}
      {(!!title || !!icon) && (
        <DialogTitle className="flex items-center gap-2 text-base">
          {icon} {title}
        </DialogTitle>
      )}
      {!noDivider && <Divider className="mb-1" inset="none" />}
      {!!description && <p className="text-sm">{description}</p>}
      <DialogContent
        className={classNames({ 'px-2': scrollGutter })}
        sx={{
          overflowX: 'hidden',
          // At least 1px: at fractional display scaling (150%) Chrome otherwise snaps the content
          // one device pixel past this scroll container and shows a scrollbar for content that fits.
          paddingBottom: !actions && scrollGutter ? '8px' : '1px',
          ...contentSx,
        }}
      >
        <minimizeTitleContext.Provider value={canMinimize ? reportTitle : null}>
          {children}
        </minimizeTitleContext.Provider>
      </DialogContent>
      {!!actions && <DialogActions>{actions}</DialogActions>}
    </ModalDialog>
  );

  return (
    <>
      <Modal
        {...rest}
        {...modalProps}
        slotProps={{
          backdrop: {
            onClick: onClickBackdrop,
            onDoubleClick: onDoubleClickBackdrop,
          },
        }}
        onClose={(_, reason) => {
          if (disableClose) return;
          if (reason === 'backdropClick') return;
          if ((reason === 'escapeKeyDown' && confirmCloseOnEscape) || confirmCloseOnClose) {
            showConfirmationModal.show();
            return;
          }
          handleClose();
        }}
      >
        {DialogWrapper ? <DialogWrapper>{dialog}</DialogWrapper> : dialog}
      </Modal>
      {showConfirmationModal.value && (
        <ConfirmationModal
          buttonProps={{ primaryFilled: true }}
          color="danger"
          icon={<WarningIcon />}
          title={t('areYouSureForm')}
          toggle={showConfirmationModal}
          onConfirm={handleClose}
        >
          <p className="pl-4 pt-4">{t('changesWillBeLost')}</p>
        </ConfirmationModal>
      )}
    </>
  );
};

export default KpcModal;
